/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * CART-09 — Mattress Protector storefront helpers.
 *
 * Mirrors the field contract emitted by the SCAPI basket hook in
 * `cartridges/sfnext_ashley_hooks/cartridge/scripts/mattressprotector/MattressProtectorEnrichment.js`.
 * Every eligibility / size-mapping / excluded-category decision happens
 * server-side; these helpers only project the resolved fields into
 * UI-friendly shapes (visibility flag, checkbox state, formatted price).
 */

import { formatCurrency } from '@/lib/currency';

/**
 * The five enrichment fields the basket hook attaches to each `productItem`.
 *
 * Fields are intentionally all optional — protector PLIs carry only
 * `c_isMattressProtector` / `c_mattressParentItemId`, ineligible mattresses
 * carry none, and eligible mattresses carry the mattress-side subset.
 */
export interface MattressProtectorFields {
    /**
     * `true` on the protector PLI itself. Used by the offer-row gate
     * (`getMattressProtectorRowState`) implicitly — protector PLIs do
     * not carry `c_protectorSku`, so they fail the visibility check and
     * the inline offer row never renders on a protector row.
     */
    c_isMattressProtector?: boolean;
    /** UUID of the mattress PLI this protector belongs to (set on the protector PLI). */
    c_mattressParentItemId?: string | null;
    /** `true` when a protector PLI is linked to this mattress PLI in the basket. */
    c_mattressProtectorSelected?: boolean;
    /** SKU of the protector that would be added for this mattress (or is already linked). */
    c_protectorSku?: string | null;
    /** Protector price — linked-PLI base price when selected, price-book price otherwise. */
    c_mattressProtectorPrice?: number | null;
    /** UUID of the linked protector PLI; only present when selected. */
    c_mattressProtectorItemId?: string | null;
}

export interface MattressProtectorRowState {
    /** Whether the storefront should render the offer row at all. */
    visible: boolean;
    /** Checkbox state — `true` when a protector PLI is linked to this mattress. */
    checked: boolean;
    /**
     * Localized price text with a leading "+" sign (e.g. `"+$49.00"`).
     * Undefined when the basket hook did not surface a price (offline
     * protector product, missing price book entry, etc.) — the offer row
     * stays visible in that case so the shopper can still opt in.
     */
    priceText: string | undefined;
}

/**
 * Project mattress-protector enrichment fields into UI props for
 * `<CartLineItemAddOnRow>`.
 *
 * @example
 *   const row = getMattressProtectorRowState(productItem, locale, currency);
 *   if (row.visible) {
 *     return <CartLineItemAddOnRow checked={row.checked} priceText={row.priceText} disabled />;
 *   }
 */
export function getMattressProtectorRowState(
    item: MattressProtectorFields | undefined | null,
    locale: string,
    currency: string
): MattressProtectorRowState {
    const selected = Boolean(item?.c_mattressProtectorSelected);

    // Cart behavior: show offer row only before add; hide once linked.
    // If the linked protector PLI is removed, enrichment sets selected=false
    // and the row naturally reappears.
    if (!item || !item.c_protectorSku || selected) {
        return { visible: false, checked: false, priceText: undefined };
    }

    const rawPrice = item.c_mattressProtectorPrice;
    const priceText =
        typeof rawPrice === 'number' && Number.isFinite(rawPrice)
            ? `+${formatCurrency(rawPrice, locale, currency)}`
            : undefined;

    return {
        visible: true,
        checked: false,
        priceText,
    };
}
